import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PrimaryButton, SecondaryButton } from "../../../components/Button";
import { Order } from "../../../types/order";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { capitalize, usdFormat } from "../../../utils";
import toast from "react-hot-toast";
import { gql } from "@apollo/client";
import CustomSelect from "../../../components/customSelect";

const BASE_API_URL = "https://api.roninpos.app/api";
const MX_BASE_API_URL = "https://api.mxmerchant.com/checkout/v3/";

const client = createApolloClient();

const UPDATE_ORDER_STATUS = gql`
  mutation updateOrderStatus($id: Int!, $reference_id: String!, $status: String!) {
    update_orders_by_pk(
      pk_columns: { id: $id, reference_id: $reference_id }
      _set: { status: $status }
    ) {
      id
      status
    }
  }
`;

const PUSH_ORDER = gql`
  mutation AddOrderNewV2($orderServiceInput: OrderServiceInput!) {
    order_service_v2(orderServiceInput: $orderServiceInput) {
      message
    }
  }
`;

const statusOptions = [
  { value: "open_tab", label: "Open Tab" },
  { value: "deleted_tab", label: "Deleted Tab" },
  { value: "in_use", label: "In Use / Locked" },
];

interface ManageTabModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
}

const getLocationConfig = (order: Order) => {
  const location = order.location as any;
  const ppc = location?.payment_processor_config;
  if (!ppc) return null;

  const config = typeof ppc.config === "string" ? JSON.parse(ppc.config) : ppc.config;
  const processor = ppc.processor || "mxmerchant";

  return { config, processor, dynamic_descriptor: location.dynamic_descriptor };
};

const buildStripeMetadata = (order: Order) => {
  const str = (val: any) => (val != null ? String(val) : "");
  return {
    reference_id: str(order.reference_id),
    android_device_id: str(order.android_device_id),
    attendee_id: str(order.attendee_id),
    build_id: str(order.build_id),
    cash_balance_charged: str(order.cash_balance_charged),
    cash_charged: str(order.cash_charged),
    credit_not_present_charged: str(order.credit_not_present_charged),
    credit_present_charged: str(order.credit_present_charged),
    digital_surcharge: str(order.digital_surcharge),
    discount: JSON.stringify(order.discount || []).slice(0, 499),
    event_id: str((order.event as any)?.id),
    is_tab: str(order.is_tab),
    location_id: str((order.location as any)?.id),
    menu_id: str(order.menu_id),
    name: str(order.name),
    nickname: str(order.nickname),
    notes: str(order.notes).slice(0, 200),
    phone_number: str(order.phone_number),
    promo_balance_charged: str(order.promo_balance_charged),
    promo_code_uid: str(order.promo_code_uid),
    split_payment: str(order.split_payment),
    status: str(order.status),
    subtotal: str(order.subtotal),
    tappit_charged: str(order.tappit_charged),
    tappit_uid: str(order.tappit_uid),
    tax: str(order.tax),
    tip: str(order.tip),
    token_value_charged: str(order.token_value_charged),
    total_discount: str(order.total_discount),
    total_promo_discount: str(order.total_promo_discount),
    uid: str(order.uid),
    user_id: str(order.user_id),
    vendor_id: str(order.vendor_id),
  };
};

const calculateTotalPaid = (order: Order) => {
  return (order.subtotal ?? 0) + (order.digital_surcharge ?? 0) + (order.tax ?? 0) + (order.tip ?? 0);
};

const toInt = (v: any) => (v == null ? undefined : Number(v));
const toCents = (v: any) => (v == null ? 0 : Number(v) || 0);

/**
 * Flatten cart items by expanding quantity into individual item entries,
 * matching the POS app's flattenCartItems behavior.
 */
const flattenCartItems = (items: any): any[] | null => {
  if (!items) return null;

  // Handle open tab format: { cartItems: [...] }
  let itemsArray: any[];
  if (items.cartItems && Array.isArray(items.cartItems)) {
    itemsArray = items.cartItems;
  } else if (Array.isArray(items)) {
    itemsArray = items;
  } else {
    return null;
  }

  if (!itemsArray.length) return null;

  const deepCopy = JSON.parse(JSON.stringify(itemsArray));

  // Expand items by quantity (quantity 3 becomes 3 separate items)
  deepCopy.forEach((obj: any, _index: number, array: any[]) => {
    if (obj.quantity >= 2) {
      for (let i = obj.quantity; i >= 2; i -= 1) {
        array.push(JSON.parse(JSON.stringify(obj)));
      }
    }
    delete obj.quantity;
    delete obj.__typename;
  });

  return deepCopy.map((val: any) => ({
    id: val.id != null ? String(val.id) : undefined,
    name: val.name ?? undefined,
    price: toCents(val.price),
    category: val.category ?? undefined,
    description: val.description ?? undefined,
    short_name: val.short_name ?? undefined,
    tax: val.tax ?? undefined,
    tax_type: val.tax_type || (val.tax === "inclusive" ? "inclusive" : "exclusive"),
    tax_percentage: val.tax_percentage != null ? Number(val.tax_percentage) : undefined,
    item_tax: toCents(val.item_tax),
    item_discount_applied: toCents(val.item_discount_applied),
    item_promo_discount_applied: toCents(val.item_promo_discount_applied),
    item_cash_balance_charged: toCents(val.item_cash_balance_charged),
    item_promo_balance_charged: toCents(val.item_promo_balance_charged),
    item_token_value_charged: toCents(val.item_token_value_charged),
    item_tappit_charged: toCents(val.item_tappit_charged),
    refunded_value: toCents(val.refunded_value),
    is_refunded: val.is_refunded ?? null,
    is_custom_item: !!val.is_custom_item,
    is_variable_price: !!val.is_variable_price,
    modifier_type: val.modifier_type ?? undefined,
    modifiers: Array.isArray(val.modifiers) ? val.modifiers : [],
    ...(val.item_modifiers_applied ? { item_modifiers_applied: val.item_modifiers_applied } : {}),
    ...(val.modifiers_update ? { modifiers_update: val.modifiers_update } : {}),
    redeemable_token_id: val.redeemable_token_id ?? undefined,
    redeemable_token_name: val.redeemable_token_name ?? undefined,
    token_price: val.token_price != null ? Number(val.token_price) : undefined,
  }));
};

/**
 * Calculate per-item tax and order-level tax total.
 * Matches the POS app's calculateTax logic: prorates discounts across items,
 * computes item_tax based on tax_percentage and tax_type, and sums into order tax.
 */
const calculateTax = (items: any[], order: Order): { items: any[]; tax: number } => {
  if (!items || !items.length) return { items: items || [], tax: 0 };

  const eligibleItems = items.filter((item: any) => !item.is_token_redeemed);
  const eligibleSubtotal = eligibleItems.reduce(
    (sum: number, item: any) => sum + (Number(item?.price) || 0),
    0
  );

  if (eligibleSubtotal <= 0) return { items, tax: 0 };

  let totalTax = 0;
  const totalDiscount = Number(order?.total_discount) || 0;
  const totalPromoDiscount = Number(order?.total_promo_discount) || 0;

  // First pass: calculate initial discount allocation using floor
  let allocatedDiscount = 0;
  let allocatedPromoDiscount = 0;

  const itemsWithInitialDiscount = items.map((item: any) => {
    const price = Number(item?.price) || 0;
    const isRedeemed = !!item?.is_token_redeemed;

    const itemDiscountShare =
      !isRedeemed && eligibleSubtotal > 0
        ? Math.floor(totalDiscount * (price / eligibleSubtotal))
        : 0;

    const itemPromoShare =
      !isRedeemed && eligibleSubtotal > 0
        ? Math.floor(totalPromoDiscount * (price / eligibleSubtotal))
        : 0;

    allocatedDiscount += itemDiscountShare;
    allocatedPromoDiscount += itemPromoShare;

    return { item, price, isRedeemed, itemDiscountShare, itemPromoShare };
  });

  // Calculate leftover discount due to rounding
  let leftoverDiscount = totalDiscount - allocatedDiscount;
  let leftoverPromoDiscount = totalPromoDiscount - allocatedPromoDiscount;

  // Second pass: allocate leftover and compute tax
  const itemsWithTax = itemsWithInitialDiscount.map((itemData: any) => {
    const { item, price, isRedeemed } = itemData;
    let { itemDiscountShare, itemPromoShare } = itemData;

    if (!isRedeemed && leftoverDiscount > 0) {
      const itemPriceAfterDiscount = price - itemDiscountShare;
      const additionalDiscount = Math.min(leftoverDiscount, itemPriceAfterDiscount);
      itemDiscountShare += additionalDiscount;
      leftoverDiscount -= additionalDiscount;
    }

    if (!isRedeemed && leftoverPromoDiscount > 0) {
      const itemPriceAfterAllDiscounts = price - itemDiscountShare - itemPromoShare;
      const additionalPromoDiscount = Math.min(leftoverPromoDiscount, itemPriceAfterAllDiscounts);
      itemPromoShare += additionalPromoDiscount;
      leftoverPromoDiscount -= additionalPromoDiscount;
    }

    const itemTaxPercentage =
      typeof item?.tax_percentage === "number" ? item.tax_percentage : 0;

    // Support either 7 or 0.07
    const taxRate = itemTaxPercentage < 1 ? itemTaxPercentage : itemTaxPercentage / 100;

    const itemPromoBalanceApplied = Number(item?.item_promo_balance_charged) || 0;
    const totalAdjustments = itemDiscountShare + itemPromoShare + itemPromoBalanceApplied;

    const inclusiveModifierTax = Number(item?.inclusive_modifier_tax) || 0;
    const modifierBaseTotal =
      inclusiveModifierTax !== 0
        ? price - Number(item?.base_price_before_modifiers || price)
        : 0;

    const taxableAmount = Math.max(price - modifierBaseTotal - totalAdjustments, 0);

    let itemTax = 0;
    if (!isRedeemed && taxRate > 0 && taxableAmount > 0) {
      itemTax = Math.round(taxableAmount * taxRate);
    }
    itemTax += inclusiveModifierTax;
    totalTax += itemTax;

    return {
      ...item,
      item_tax: itemTax,
      item_discount_applied: itemDiscountShare,
      item_promo_discount_applied: itemPromoShare,
    };
  });

  return { items: itemsWithTax, tax: Math.max(totalTax, 0) };
};

/**
 * Calculate digital surcharge (service fee) based on service_fee_percentage.
 * Matches the POS app's calculateServiceFee logic.
 * Only applies to non-cash (credit) payments.
 */
const calculateServiceFee = (
  subtotal: number,
  tip: number,
  tax: number,
  serviceFeePercentage: number,
  totalDiscount: number = 0,
  totalPromoDiscount: number = 0,
  cashBalanceCharged: number = 0,
  promoBalanceCharged: number = 0,
  tokenValueCharged: number = 0,
): number => {
  if (serviceFeePercentage <= 0) return 0;

  const amountForCard = Math.max(
    subtotal + tip + tax
    - totalDiscount - totalPromoDiscount
    - cashBalanceCharged - promoBalanceCharged
    - tokenValueCharged,
    0
  );

  return Math.ceil((amountForCard * serviceFeePercentage) / 100);
};

/**
 * Build the enriched order with payment transaction data for Stripe responses.
 */
const applyTransactionApprovalValuesStripe = (order: Order, transactionData: any) => {
  const payment_processor_reference_id = transactionData?.response?.id ?? transactionData?.id;
  const credit_present_charged = transactionData?.response?.amount ?? transactionData?.amount ?? calculateTotalPaid(order);
  const now = new Date().toISOString();

  const paymentResponseData = transactionData?.response ?? transactionData;
  delete paymentResponseData?.metadata;

  return {
    payment_processor_reference_id,
    credit_present_charged,
    transaction_at: now,
    transaction_time: now,
    payments: {
      payment_data: paymentResponseData,
      payment_type: "credit",
      reference_id: order.reference_id,
      status: "approved",
      amount: credit_present_charged,
    },
  };
};

/**
 * Build the enriched order with payment transaction data for MX responses.
 */
const applyTransactionApprovalValuesMX = (order: Order, transactionData: any) => {
  const payment_processor_reference_id = (transactionData?.externalId ?? transactionData?.id)?.toString();
  const credit_present_charged = Math.ceil(
    parseFloat((parseFloat(transactionData?.totalAmount ?? transactionData?.amount) * 100).toFixed(4))
  );
  const now = new Date().toISOString();

  // Strip large/internal fields
  const fieldsToRemove = ["onlineTransactionEmvPayload", "customFields", "endpoint", "uuid", "warnings", "terminalResponse"];
  fieldsToRemove.forEach((field) => delete transactionData[field]);
  delete transactionData?.gatewayResponse?.rawResponse?.meta;
  delete transactionData?.meta;

  return {
    payment_processor_reference_id,
    credit_present_charged,
    transaction_at: now,
    transaction_time: now,
    payments: {
      payment_data: transactionData,
      payment_type: "credit",
      reference_id: order.reference_id,
      status: "approved",
      amount: credit_present_charged,
    },
  };
};

/**
 * Build the full OrderServiceInput payload to sync the order via order_service_v2,
 * matching the POS app's buildOrderServiceInput structure.
 */
const buildOrderServiceInput = (
  order: Order,
  paymentData: {
    payment_processor_reference_id: string;
    credit_present_charged: number;
    transaction_at: string;
    transaction_time: string;
    payments: any;
  },
  calculatedTax: number,
  taxedItems: any[] | null,
  tipAmount: number,
  digitalSurcharge: number,
) => {
  const discount = order.discount ?? null;

  return {
    android_device_id: order.android_device_id ?? null,
    attendee_id: toInt(order.attendee_id),
    build_id: order.build_id ?? null,

    cash_balance_charged: toCents(order.cash_balance_charged),
    cash_charged: toCents(order.cash_charged),
    credit_not_present_charged: toCents(order.credit_not_present_charged),
    credit_present_charged: toCents(paymentData.credit_present_charged),

    digital_surcharge: digitalSurcharge,

    discount,

    event_id: toInt((order.event as any)?.id),
    is_tab: true,

    items: taxedItems,

    location_id: toInt((order.location as any)?.id),
    menu_id: toInt(order.menu_id),

    name: order.name ?? null,
    nickname: order.nickname ?? null,
    notes: order.notes ?? null,

    payment_processor_reference_id: paymentData.payment_processor_reference_id ?? null,
    payments: paymentData.payments,

    phone_number: order.phone_number ?? null,

    promo_balance_charged: toCents(order.promo_balance_charged),

    promo_code: order.promo_code ?? null,
    promo_code_uid: order.promo_code_uid ?? null,

    reference_id: String(order.reference_id),
    split_payment: !!order.split_payment,

    status: "processed",

    subtotal: toCents(order.subtotal),
    tappit_charged: toCents(order.tappit_charged),
    tappit_uid: order.tappit_uid ?? null,
    tax: calculatedTax,
    tip: tipAmount,
    token_value_charged: toCents(order.token_value_charged),

    tokens_redeemed: order.tokens_redeemed ?? null,
    total_discount: toCents(order.total_discount),
    total_promo_discount: toCents(order.total_promo_discount),

    transaction_at: paymentData.transaction_at,
    transaction_time: paymentData.transaction_time,

    uid: order.uid ?? null,
    user_id: toInt(order.user_id),
    vendor_id: toInt(order.vendor_id),
  };
};

export default function ManageTabModal({
  isOpen,
  onClose,
  order,
}: ManageTabModalProps) {
  const initialFocusRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>(order.status);
  const [tipPercentage, setTipPercentage] = useState<number | null>(0);
  const [customTipAmount, setCustomTipAmount] = useState<string>("");

  const hasVaultedCard = order.attendee?.card_on_files?.length > 0;
  const card = hasVaultedCard ? (order.attendee.card_on_files[0] as any) : null;
  const lastFour = card?.last_four_digits ?? null;

  const subtotal = order.subtotal ?? 0;

  // Calculate tip amount in cents
  const tipAmount = tipPercentage !== null
    ? Math.round(subtotal * (tipPercentage / 100))
    : Math.round((parseFloat(customTipAmount) || 0) * 100);

  // Pre-calculate tax for display (flatten items + calculate)
  const flattenedItemsForDisplay = flattenCartItems(order.items);
  const { tax: previewTax } = calculateTax(flattenedItemsForDisplay || [], order);

  // Calculate service fee for display
  const location = order.location as any;
  const serviceFeePercentage = location?.digital_surcharge_percentage || 0;
  const previewServiceFee = calculateServiceFee(
    subtotal,
    tipAmount,
    previewTax,
    serviceFeePercentage,
    order.total_discount ?? 0,
    order.total_promo_discount ?? 0,
    order.cash_balance_charged ?? 0,
    order.promo_balance_charged ?? 0,
    order.token_value_charged ?? 0,
  );

  // Total that will be charged
  const totalToCharge = subtotal + previewTax + tipAmount + previewServiceFee
    - (order.total_discount ?? 0) - (order.total_promo_discount ?? 0)
    - (order.cash_balance_charged ?? 0) - (order.promo_balance_charged ?? 0)
    - (order.token_value_charged ?? 0);

  // Pull tip percentages from menu config, filter out empty/zero values
  const menu = order.menu as any;
  const menuTipPercentages = [
    menu?.tip_percentage_1,
    menu?.tip_percentage_2,
    menu?.tip_percentage_3,
  ].filter(Boolean).sort((a: number, b: number) => a - b);
  const hasTips = menu?.is_tips ?? false;

  const handleUpdateStatus = async () => {
    if (selectedStatus === order.status) {
      toast.error("Please select a different status.");
      return;
    }
    setIsLoading(true);
    try {
      await client.mutate({
        mutation: UPDATE_ORDER_STATUS,
        variables: {
          id: order.id,
          reference_id: order.reference_id,
          status: selectedStatus,
        },
      });
      toast.success(`Tab status updated to "${capitalize(selectedStatus)}"`);
      await client.clearStore();
      onClose();
      window.location.reload();
    } catch (error) {
      console.error("Error updating tab status:", error);
      toast.error("Failed to update tab status: " + error);
    } finally {
      setIsLoading(false);
    }
  };

  const processStripePayment = async (chargeAmount?: number) => {
    const locationConfig = getLocationConfig(order);
    if (!locationConfig) throw new Error("No payment processor config found on location");

    const { config, dynamic_descriptor } = locationConfig;

    const paymentMethodId = card.payment_data?.paymentMethodId || card.payment_data?.payment_method_id;
    if (!paymentMethodId) throw new Error("No payment method ID found on vaulted card");

    const connectedAccountId = card.payment_data?.connectedAccountId || config?.connected_account_id;
    if (!connectedAccountId) throw new Error("No connected account ID found");

    const isTerminalCard = !!card.payment_data?.connectedAccountId;
    const customerId = order.attendee?.customer_id || null;

    // Calculate application fee using the charge amount (includes tax)
    const amountToCharge = chargeAmount ?? calculateTotalPaid(order);
    const processingRate = parseFloat(config?.processing_rate || "0");
    const perTxnFee = (config?.application_fee_per_transaction || 0) * 100;
    const applicationFeeAmount = Math.ceil(amountToCharge * processingRate) + perTxnFee;

    // Statement descriptor
    const rawDescriptor = dynamic_descriptor ?? "";
    const cleanedDescriptor = rawDescriptor.replace(/\*/g, "").trim();
    const statementDescriptorSuffix = cleanedDescriptor.length > 0
      ? cleanedDescriptor.substring(0, 22) : undefined;

    const metadata = buildStripeMetadata(order);

    const response = await fetch(`${BASE_API_URL}/Stripe/ChargeCardOnFile`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        localOrder: { ...order, total_paid: amountToCharge },
        payment_method: paymentMethodId,
        connected_account: connectedAccountId,
        platform_customer: isTerminalCard ? null : customerId,
        connected_customer: isTerminalCard ? customerId : null,
        currency: config?.currency || "usd",
        application_fee_amount: applicationFeeAmount,
        statement_descriptor: statementDescriptorSuffix,
        metadata,
      }),
    });

    const data = await response.json();
    if (!response.ok || data.status !== 200) {
      throw new Error(data?.response?.message || data?.message || "Stripe card charge failed");
    }

    return data;
  };

  const processMXPayment = async (chargeAmount?: number) => {
    const locationConfig = getLocationConfig(order);
    if (!locationConfig) throw new Error("No payment processor config found on location");

    const { config } = locationConfig;
    const { consumerKey, secretKey, merchantId } = config || {};

    if (!consumerKey || !secretKey) throw new Error("MX credentials not found on location config");

    const token = card.payment_data?.token;
    if (!token) throw new Error("No card token found on vaulted card");

    const encodedCredentials = btoa(`${consumerKey}:${secretKey}`);
    const amountToCharge = chargeAmount ?? calculateTotalPaid(order);
    const amount = (amountToCharge / 100).toFixed(2);

    const metaData = {
      order,
      new_order_payload: true,
      notification_create: true,
    };

    const response = await fetch(`${MX_BASE_API_URL}payment?echo=true`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        Authorization: `Basic ${encodedCredentials}`,
      },
      body: JSON.stringify({
        merchantId,
        amount,
        paymentType: "Sale",
        tenderType: "Card",
        cardAccount: { token },
        customer: { id: order.attendee?.customer_id },
        meta: JSON.stringify(metaData),
        invoice: order.reference_id,
      }),
    });

    const data = await response.json();
    if (data?.status !== "Approved") {
      throw new Error(data?.message || `MX payment ${data?.status || "failed"}`);
    }

    return data;
  };

  const handleCloseTab = async () => {
    if (!hasVaultedCard) {
      toast.error("No vaulted card found on this attendee.");
      return;
    }

    const locationConfig = getLocationConfig(order);
    if (!locationConfig) {
      toast.error("No payment processor config found on this location.");
      return;
    }

    setIsLoading(true);
    try {
      // 1. Flatten items
      const flattenedItems = flattenCartItems(order.items);

      // 2. Calculate tax on flattened items
      const { items: taxedItems, tax: calculatedTax } = calculateTax(
        flattenedItems || [],
        order
      );

      // 3. Calculate service fee (includes tip in its base, matching POS flow)
      const digitalSurcharge = calculateServiceFee(
        subtotal,
        tipAmount,
        calculatedTax,
        serviceFeePercentage,
        order.total_discount ?? 0,
        order.total_promo_discount ?? 0,
        order.cash_balance_charged ?? 0,
        order.promo_balance_charged ?? 0,
        order.token_value_charged ?? 0,
      );

      // 4. Calculate final charge total: subtotal + tax + tip + service fee - deductions
      const chargeTotal = Math.max(
        subtotal + calculatedTax + tipAmount + digitalSurcharge
        - (order.total_discount ?? 0) - (order.total_promo_discount ?? 0)
        - (order.cash_balance_charged ?? 0) - (order.promo_balance_charged ?? 0)
        - (order.token_value_charged ?? 0),
        0
      );

      // 5. Charge the vaulted card
      const isStripe = locationConfig.processor === "stripe";
      let chargeResponse: any;
      if (isStripe) {
        chargeResponse = await processStripePayment(chargeTotal);
      } else {
        chargeResponse = await processMXPayment(chargeTotal);
      }

      // 6. Build payment transaction data from charge response
      const paymentData = isStripe
        ? applyTransactionApprovalValuesStripe(order, chargeResponse)
        : applyTransactionApprovalValuesMX(order, chargeResponse);

      // 7. Build full OrderServiceInput and sync via order_service_v2
      const orderServiceInput = buildOrderServiceInput(
        order,
        paymentData,
        calculatedTax,
        taxedItems.length ? taxedItems : flattenedItems,
        tipAmount,
        digitalSurcharge,
      );

      await client.mutate({
        mutation: PUSH_ORDER,
        variables: { orderServiceInput },
      });

      toast.success("Tab closed and payment processed successfully.");
      await client.clearStore();
      onClose();
      window.location.reload();
    } catch (error: any) {
      console.error("Error closing tab:", error);
      toast.error("Failed to close tab: " + (error.message || error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[1000]"
        initialFocus={initialFocusRef}
        onClose={isLoading ? () => {} : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-[1000]"
            aria-hidden="true"
            style={{ backgroundColor: "rgba(0,0,0,0.2)" }}
          />
        </Transition.Child>

        <div className="fixed inset-0 z-[1001] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative z-[1002] transform overflow-hidden rounded-lg bg-white px-6 py-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <div ref={initialFocusRef} tabIndex={-1} aria-hidden="true" />

                <Dialog.Title
                  as="h3"
                  className="text-xl font-semibold leading-6 text-gray-900 mb-4"
                >
                  Manage Tab
                </Dialog.Title>

                <div className="space-y-4">
                  {/* Tab Details */}
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="font-semibold text-gray-700 mb-2">Tab Details</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tab Name:</span>
                        <span className="font-medium">{order.name || "—"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tab Nickname:</span>
                        <span className="font-medium">{order.nickname || "—"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Status:</span>
                        <span className="font-medium">{capitalize(order.status)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Attendee:</span>
                        <span className="font-medium">
                          {order.attendee
                            ? [order.attendee.first_name, order.attendee.last_name].filter(Boolean).join(" ") || "—"
                            : "No attendee"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Card on File:</span>
                        <span className="font-medium">
                          {hasVaultedCard ? `•••• ${lastFour}` : "None"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">{usdFormat.format(subtotal / 100)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Update Status */}
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="font-semibold text-gray-900 mb-2">Update Tab Status</h4>
                    <div className="mb-3">
                      <CustomSelect
                        options={statusOptions}
                        value={selectedStatus}
                        onChange={(value) => setSelectedStatus(value)}
                        title="New Status"
                      />
                    </div>
                    <div className="flex justify-end">
                      <PrimaryButton
                        isLoading={isLoading}
                        isDisabled={selectedStatus === order.status}
                        onClick={handleUpdateStatus}
                      >
                        Update Status
                      </PrimaryButton>
                    </div>
                  </div>

                  {/* Close Tab — hide if already processed to prevent double charges */}
                  {order.status !== "processed" && (
                  <div className={`p-4 rounded-md ${hasVaultedCard ? "bg-red-50" : "bg-red-50 border border-red-200"}`}>
                    <h4 className="font-semibold mb-3 text-red-900">
                      Close Tab
                    </h4>

                    {!hasVaultedCard ? (
                      <p className="text-sm text-red-700">
                        Cannot close tab — no vaulted card found on the attendee. A card must be vaulted before the tab can be closed.
                      </p>
                    ) : (
                      <>
                        {/* Tip Selection */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-red-900 mb-2">
                            Add Tip {!hasTips && <span className="font-normal text-red-600">(Tips not enabled on menu)</span>}
                          </label>
                          <div className="flex gap-2 mb-2">
                            <button
                              type="button"
                              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                                tipPercentage === 0
                                  ? "bg-red-600 text-white border-red-600"
                                  : "bg-white text-red-700 border-red-300 hover:bg-red-100"
                              }`}
                              onClick={() => {
                                setTipPercentage(0);
                                setCustomTipAmount("");
                              }}
                            >
                              No Tip
                            </button>
                            {menuTipPercentages.map((pct: number) => (
                              <button
                                key={pct}
                                type="button"
                                className={`flex-1 px-2 py-1.5 text-sm rounded-md border transition-colors ${
                                  tipPercentage === pct
                                    ? "bg-red-600 text-white border-red-600"
                                    : "bg-white text-red-700 border-red-300 hover:bg-red-100"
                                }`}
                                onClick={() => {
                                  setTipPercentage(pct);
                                  setCustomTipAmount("");
                                }}
                              >
                                {pct}%
                                <span className="block text-xs opacity-75">
                                  {usdFormat.format(Math.round(subtotal * (pct / 100)) / 100)}
                                </span>
                              </button>
                            ))}
                            <button
                              type="button"
                              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                                tipPercentage === null
                                  ? "bg-red-600 text-white border-red-600"
                                  : "bg-white text-red-700 border-red-300 hover:bg-red-100"
                              }`}
                              onClick={() => setTipPercentage(null)}
                            >
                              Custom
                            </button>
                          </div>
                          {tipPercentage === null && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-red-700">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={customTipAmount}
                                onChange={(e) => setCustomTipAmount(e.target.value)}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                className="w-32 px-2 py-1.5 text-sm border border-red-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500"
                              />
                            </div>
                          )}
                        </div>

                        {/* Charge Breakdown */}
                        <div className="bg-white bg-opacity-60 rounded-md p-3 mb-3 space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-red-700">Subtotal:</span>
                            <span className="font-medium text-red-900">{usdFormat.format(subtotal / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-red-700">Tax:</span>
                            <span className="font-medium text-red-900">{usdFormat.format(previewTax / 100)}</span>
                          </div>
                          {tipAmount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-red-700">Tip{tipPercentage !== null ? ` (${tipPercentage}%)` : ""}:</span>
                              <span className="font-medium text-red-900">{usdFormat.format(tipAmount / 100)}</span>
                            </div>
                          )}
                          {previewServiceFee > 0 && (
                            <div className="flex justify-between">
                              <span className="text-red-700">Service Fee ({serviceFeePercentage}%):</span>
                              <span className="font-medium text-red-900">{usdFormat.format(previewServiceFee / 100)}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t border-red-200 pt-1 mt-1">
                            <span className="font-semibold text-red-900">Total Charge:</span>
                            <span className="font-semibold text-red-900">{usdFormat.format(Math.max(totalToCharge, 0) / 100)}</span>
                          </div>
                        </div>

                        <p className="text-sm mb-3 text-red-700">
                          This will charge {usdFormat.format(Math.max(totalToCharge, 0) / 100)} to the vaulted card ending in {lastFour} and close the tab.
                        </p>
                      </>
                    )}

                    <div className="flex justify-end">
                      <PrimaryButton
                        isLoading={isLoading}
                        isDisabled={!hasVaultedCard}
                        onClick={handleCloseTab}
                      >
                        Close Tab
                      </PrimaryButton>
                    </div>
                  </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-end">
                  <SecondaryButton onClick={onClose} isDisabled={isLoading}>
                    Cancel
                  </SecondaryButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
